// @flow
import React, { useEffect, useState } from "react";
import "./styles.css";
import Coordinates from "../../models/coordinates";
import TextBox from "../text-box";
import TextBlock from "../text-block";
import AddressControl from "../address-control";
import { LocationControl } from "../location-control/location-control";
import FAB from "../fab";
import * as R from "ramda";
import TrashCollectionSite from "../../models/trash-collection-site";
import SupplyDistributionSite from "../../models/supply-distribution-site";
import Celebration from "../../models/celebration";
import classNames from "classnames";
import Spinner from "../spinner";
import DateTimePicker from "../date-time-picker";


type RowPropsType = {
    site: Object,
    onChange: (string, any) => void,
    remove: any => void,
    markerType: string
};


export const TownSitePanel = ({ onChange, remove, site, markerType }: RowPropsType) => {
    const { address, name, notes, coordinates, start, end } = site;
    return (
        <article className={ "town-site-editor--location" }>
            <header className={ "town-site-editor--location-header" }>
                <FAB className="town-site-editor--delete-button" icon={ "faTrashAlt" } onClick={ remove }/>
            </header>
            <TextBox
                labelText={ "Name" }
                onChange={ onChange("name") }
                value={ name }
            />
            <TextBlock
                labelText={ "Notes" }
                onChange={ onChange("notes") }
                value={ notes }/>
            <div className={ "town-site-editor--start-end" }>
                <DateTimePicker
                    className={ "town-site-editor-location-start" }
                    value={ start }
                    type={ "datetime-local" }
                    onChange={ onChange("start") }
                    labelText={ "Start" }
                />
                <DateTimePicker
                    className={ "town-site-editor-location-end" }
                    value={ end }
                    type={ "datetime-local" }
                    onChange={ onChange("end") }
                    labelText={ "End" }/>
            </div>
            <AddressControl
                className={ "town-site-editor--small-text" }
                labelText={ "Site Address" }
                onChange={ onChange("address") }
                value={ address }
            />

            <LocationControl
                labelText={ "Site Location" }
                pinLabel={ name }
                markerType={ markerType }
                onChange={ onChange("coordinates") }
                coordinates={ coordinates }/>
        </article>
    );
};

type PropsType = {
    labelText: ?string,
    locations: { [string]: Celebration | TrashCollectionSite | SupplyDistributionSite },
    onChange: any => void,
    onSave: any=>void,
    onDelete: any=>void,
    townId: string,
    Model: Celebration | TrashCollectionSite | SupplyDistributionSite,
    markerType: string
};

export const TownSiteEditor = ({ townId, locations = {}, labelText, Model, onSave, onDelete, markerType = "DEFAULT" }: PropsType) => {

    const [myLocations, setMyLocations] = useState([]);
    const [hasDeletions, setHasDeletions] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const newLocation = (): void => {
        setMyLocations([Model.create({ isDirty: true, townId })].concat(myLocations));
    };

    const saveLocations = (): void => {
        setIsBusy(true);
        myLocations.forEach(location => {
            if (location.isDirty) {
                onSave(Model.create(location));
            }
        });
        const newLocationIds = myLocations.map(location => location.id);
        Object.keys(locations)
            .filter(id => newLocationIds.indexOf(id) === -1)
            .forEach(id => {
                onDelete(id);
            });
    };

    const removeLocation = (index: number) => () => {
        // eslint-disable-next-line no-alert
        if (window.confirm("Are you sure you want to delete this Location?")) {
            const newLocations = [].concat(myLocations.slice(0, index)).concat(myLocations.slice(index + 1));
            setMyLocations(newLocations);
            setHasDeletions(true);
        }
    };

    const handleLocationStateChange = R.curry((index: number, key: string, value: string): void => {
        const newMyLocations = [].concat(myLocations.slice(0, index), {
            ...myLocations[index],
            [key]: value,
            isDirty: true
        }, myLocations.slice(index + 1));
        setMyLocations(newMyLocations);
    });


    useEffect(() => {
        const newLocations = Object.entries(locations).map(entry => Model.create(entry[1], entry[0]));
        setMyLocations(newLocations);
        setHasDeletions(false);
        setIsBusy(false);
    }, [locations]);

    const hasDirty = myLocations.some(location => location.isDirty);
    return (
        <section className={ "town-site-editor" }>
            <header className={ "town-site-editor--header" }>
                { Boolean(labelText) ? (<h3>{ labelText }</h3>) : null }
                <FAB
                    className="town-site-editor--new-button"
                    icon={ "faPlus" }
                    onClick={ newLocation }/>
                <FAB
                    className={ "town-site-editor--save-button" }
                    icon={ "faSave" }
                    disabled={ !hasDirty && !hasDeletions }
                    onClick={ saveLocations }/>
            </header>
            <div className={ "town-site-editor--main" }>
                {
                    myLocations.map((location, i) => (
                        <TownSitePanel
                            key={ i }
                            site={ location }
                            onChange={ handleLocationStateChange(i) }
                            onSave={ saveLocations }
                            remove={ removeLocation(i) }
                            markerType={ markerType }
                        />)
                    )
                }
            </div>
            <div className={ classNames("town-site-editor--is-busy", { "is-active": isBusy }) }>
                <Spinner containerStyle={ { position: "absolute", zIndex: 1010 } }/>
            </div>
        </section>
    );
};


